import { bind as bindApiService } from '@monofe/apiservice';
import streamAPIBinder from '@monofe/apiservice/util/stream';
import { bindAllTo as bindUtil, bindContainer, buildContainer } from '@monofe/util';
import { bind as bindWorker, injectServiceWorkerController } from '@monofe/worker';
import { lazy, StrictMode, Suspense } from 'react';
import { render as DOMrender } from 'react-dom';
import type { ReportHandler } from 'web-vitals';
import { bind as localBindConfig } from './config/bind';
import localBindController from './controllers/binder';
import { injectConfigRoute } from './interface/config';
import { IContainer } from './interface/container';
import { injectRouter } from './interface/route';
import { injectENV, injectLog } from './interface/util';
import { bind as localBindRouter } from './routes/bind';
import { injectStoresRenderer } from './state';
import localBindStores from './state/bind';

const PATH_SERVICE_WORKER = 'service-worker.js';
const SCOPE_SERVICE_WORKER_DEFAULT = '.';

const _isProduction = (container: IContainer) =>
    injectENV(container).getString(ENV_BUILDTYPE) === 'production';
const _isDevelopment = (container: IContainer) => !_isProduction(container);

const ID_ROOT_ELEMENT = 'root';
const LOADING_OUTER_CLASSNAME = 'OnBootCenterModal OnBootLoadingStyle';
const LOADING_INNER_CLASSNAME = 'OnBootSpinner';
const ENV_IS_SW_ENABLED = 'REACT_APP_SW_ENABLED';
const ENV_SW_OVERRIDE = 'REACT_APP_SWFORCE';
const ENV_BUILDTYPE = 'NODE_ENV';

function LoadingView(): JSX.Element {
    return (
        <div className={LOADING_OUTER_CLASSNAME}>
            <div className={LOADING_INNER_CLASSNAME}></div>
        </div>
    );
}

async function loadContainerBindings(container: IContainer): Promise<void> {
    const promiseWrapper = (
        binder: (container: IContainer) => void,
    ): ((container: IContainer) => Promise<void>) => {
        return (targetContainer: IContainer) => {
            binder(container);
            return Promise.resolve();
        };
    };

    /* if there's a streaming media.json go find it & update the apiservice to use it */
    const bindStreamAPI = (container: IContainer) => {
        const env = injectENV(container);
        const schemaUrl = env.getString('REACT_APP_STREAMMEDIA_SCHEMAURL');

        return schemaUrl ? streamAPIBinder(schemaUrl) : Promise.resolve();
    };

    const binders: Array<(container: IContainer) => Promise<void>> = [
        bindUtil,
        localBindConfig,
        promiseWrapper(bindApiService),
        promiseWrapper(bindWorker),
        bindStreamAPI,
        localBindRouter,
        /* bindMediaPlayer, 
            placeholder, including path results in files bundled into static/js/main*.js increasing initial bundle size */
        () => Promise.resolve(),
        localBindStores,
        localBindController,
    ];

    for (const binder of binders) {
        await binder(container);
    }
}

async function startServiceWorker(container: IContainer): Promise<void> {
    const env = injectENV(container);
    const isProduction = env.getString(ENV_BUILDTYPE) === 'production';
    /* for testing, run `yarn startsw` to force the Service Worker to run in development */
    const swEnableOverride = env.getBoolean(ENV_SW_OVERRIDE);
    const hasServiceWorker = 'serviceWorker' in navigator;
    const swEnabled = env.getBoolean(
        ENV_IS_SW_ENABLED,
        true,
    ); /* if missing assume YES, otherwise allow for ENV to disable service-workers (Agnelli) */

    if (!swEnabled) {
    } else if (!hasServiceWorker) {
    } else if (isProduction || swEnableOverride) {
        const controller = await injectServiceWorkerController(container);

        /* limit SW visibility to base subpath of PWA, scope *must* end with slash '/qc/mauto' -> '/qc/mauto/' */
        let scope = env.getString('REACT_APP_ROUTE_BASENAME') ?? SCOPE_SERVICE_WORKER_DEFAULT;

        if (!scope.endsWith('/')) {
            scope = scope + '/';
        }

        controller.start(PATH_SERVICE_WORKER, scope);
    }
}

function startRenderer(container: IContainer): Promise<void> {
    const router = injectRouter(container);
    const config = injectConfigRoute(container);

    const allRoutes = router.allRoutes();

    const StoreProvider = injectStoresRenderer().render;

    const App = lazy(() => import('./app'));

    DOMrender(
        <StrictMode>
            <StoreProvider>
                <Suspense fallback={LoadingView}>
                    <App basename={config.basename} routes={allRoutes} />
                </Suspense>
            </StoreProvider>
        </StrictMode>,
        document.getElementById(ID_ROOT_ELEMENT),
    );

    return Promise.resolve();
}

declare global {
    interface Window {
        __REACT_DEVTOOLS_GLOBAL_HOOK__: Record<string, unknown> | undefined;
        __WS_DEVTOOLS_CONTAINER__: IContainer | undefined;
    }
}

function disableReactDevTools(container: IContainer): void {
    const env = injectENV(container);
    const isProduction = env.getString(ENV_BUILDTYPE) === 'production';

    const reactDevTools = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;

    if (isProduction && reactDevTools) {
        const noop = (): void => undefined;

        for (const [key, value] of Object.entries(reactDevTools)) {
            reactDevTools[key] = typeof value === 'function' ? noop : null;
        }
    }
}

/* start */
const container = buildContainer();
bindContainer(container);

loadContainerBindings(container)
    .then(_ => startServiceWorker(container))
    .then(_ => startRenderer(container))
    .then(_ => {
        if (window === undefined) {
            /* ignore */
        } else if (_isDevelopment(container)) {
            /* development mode */
            window.__WS_DEVTOOLS_CONTAINER__ = container;
        } else {
            /* production mode */
            disableReactDevTools(container);
        }

        injectLog(container).milestone('App running');
    })
    .catch(error => {
        injectLog(container).error(`Failed to start app: ${[error]}`);
    });

/* If you want to start measuring performance in your app, pass a function
   to log results (for example: reportWebVitals(console.log))
   or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals */
if (_isDevelopment(container)) {
    const reportWebVitals = (onPerfEntry?: ReportHandler) => {
        if (onPerfEntry && onPerfEntry instanceof Function) {
            import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
                getCLS(onPerfEntry);
                getFID(onPerfEntry);
                getFCP(onPerfEntry);
                getLCP(onPerfEntry);
                getTTFB(onPerfEntry);
            });
        }
    };

    reportWebVitals();
}
